import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Drawer from '@mui/material/Drawer';
import TvIcon from '@mui/icons-material/Tv';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { yomiriCast } from '../../cast/YomiriCastChannel.ts';
import { yomiriP2P } from '../../cast/YomiriP2PBridge.ts';
import { normalizeCastCode } from '../../cast/YomiriP2PBridge.ts';
import { tvPairingSync } from '../../network/pairing/TvPairingSync.ts';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import Button from '@mui/material/Button';
import { YomiriQrScannerModal } from './YomiriQrScannerModal.tsx';
import Snackbar from '@mui/material/Snackbar';

export function YomiriMobileRemote() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(false);
  const [search, setSearch] = useState('');
  const [playing, setPlaying] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [p2pState, setP2pState] = useState<'disconnected' | 'connecting' | 'connected'>(yomiriP2P.getState());
  const [linkedTvName, setLinkedTvName] = useState<string | null>(yomiriP2P.getPeerName());
  const [showPinBox, setShowPinBox] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [feedback,setFeedback]=useState('');
  useEffect(() => {
    const unsub = tvPairingSync.subscribe((status) => {
      if (status.lastMessage) setFeedback(status.lastMessage.slice(0, 150));
    });
    return unsub;
  }, []);
  useEffect(()=>{
    if(p2pState!=='connected')return;
    let lock:WakeLockSentinel|undefined,disposed=false;
    const acquire=()=>{if(document.visibilityState==='visible')void navigator.wakeLock?.request('screen').then(value=>{if(disposed)void value.release();else lock=value;}).catch(()=>{});};
    acquire();document.addEventListener('visibilitychange',acquire);
    return()=>{disposed=true;document.removeEventListener('visibilitychange',acquire);void lock?.release();};
  },[p2pState]);

  useEffect(() => {
    const linkCode=new URLSearchParams(location.hash.slice(1)).get('cast');
    if(linkCode){setPinInput(normalizeCastCode(linkCode));yomiriP2P.connectToHost(linkCode);history.replaceState(null,'',location.pathname+location.search);}
    // Notify TV that remote is online
    yomiriCast.initRole('remote', navigator.userAgent.includes('iPhone') ? 'iPhone' : 'Móvil');
    setActive(true);

    const onPeer = () => setActive(true);
    window.addEventListener('yomiri-cast-peer-connected', onPeer);

    const unsubP2PState = yomiriP2P.onState((s, peerName) => {
      setP2pState(s);
      setLinkError(yomiriP2P.getError());
      if (peerName) setLinkedTvName(peerName);
    });

    return () => {
      window.removeEventListener('yomiri-cast-peer-connected', onPeer);
      unsubP2PState();
    };
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!search.trim()) return;
    dispatchCast({ type: 'NAVIGATE', view: 'explore' });
    dispatchCast({ type: 'SEARCH', query: search.trim() });
  };

  const sendKey = (key: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Enter' | 'Escape') => {
    dispatchCast({ type: 'DPAD_NAVIGATE', key });
  };

  const dispatchCast = (cmd: any) => {
    if(!yomiriCast.send(cmd)){
      setShowPinBox(true);
      setFeedback('Vinculá una TV para enviar el control.');
    } else {
      tvPairingSync.markSending('Enviando a la TV…');
      setFeedback('Enviando a la TV…');
    }
  };

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener('yomiri-open-tv-remote', handleOpen);
    return () => window.removeEventListener('yomiri-open-tv-remote', handleOpen);
  }, []);

  return (
    <>
      <Snackbar open={!!feedback} message={feedback} autoHideDuration={2500} onClose={()=>setFeedback('')} sx={{bottom:'85px!important'}}/>
      {/* Slide-up Remote Drawer */}
      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'var(--y-containerLow, #14131d)',
              color: '#ffffff',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              p: 2.5,
              pb: 'calc(24px + env(safe-area-inset-bottom))',
              maxWidth: 480,
              mx: 'auto',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TvIcon sx={{ color: 'var(--y-primary, #ff375f)', fontSize: 22 }} />
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 750 }}>Control Remoto Yomiri</Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color:
                    p2pState === 'connected'
                      ? '#34d399'
                      : p2pState === 'connecting'
                      ? '#f59e0b'
                      : 'var(--y-onSurfaceVariant, #9e9ca8)',
                }}
              >
                {p2pState === 'connected'
                  ? `● Conectado con ${linkedTvName || 'la TV'}`
                  : p2pState === 'connecting'
                  ? '○ Conectando a la TV…'
                  : '○ Desconectado (ingresá el PIN de la TV)'}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'var(--y-onSurfaceVariant)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* PIN Linker for External Smart TVs */}
        <Box sx={{ mb: 2, p: 1.25, borderRadius: '10px', bgcolor: 'var(--y-containerHigh, #25202c)', border: '1px solid var(--y-outlineVariant, rgba(255, 255, 255, 0.08))' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VpnKeyIcon sx={{ color: 'var(--y-primary, #ff375f)', fontSize: 16 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--y-onSurface, #f4edf2)' }}>
                {p2pState === 'connected' ? `Vinculado (${yomiriP2P.getPin() ? 'PIN activo' : 'Conectado'})` : 'Vincular Smart TV'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {p2pState !== 'connected' && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setScannerOpen(true)}
                  startIcon={<QrCodeScannerIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    fontSize: 11,
                    textTransform: 'none',
                    borderRadius: '8px',
                    borderColor: 'var(--y-primary, #ff375f)',
                    color: 'var(--y-primary, #ff375f)',
                    py: 0.25,
                    px: 1,
                    fontWeight: 700,
                  }}
                >
                  Escanear QR
                </Button>
              )}
              <Button
                size="small"
                onClick={() => setShowPinBox(!showPinBox)}
                sx={{ fontSize: 11, textTransform: 'none', color: 'var(--y-primary, #ff375f)', p: 0.5 }}
              >
                {showPinBox ? 'Cerrar' : p2pState === 'connected' ? 'Cambiar código' : 'Ingresar código'}
              </Button>
            </Box>
          </Box>

          {showPinBox && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Código de la TV (20 caracteres)"
                value={pinInput}
                onChange={(e) => setPinInput(normalizeCastCode(e.target.value).slice(0, 20))}
                slotProps={{ htmlInput: { maxLength: 20, autoCapitalize:'characters', autoCorrect:'off', style: { textAlign: 'center', letterSpacing: '2px', fontWeight: 800 } } }}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: 36, fontSize: 14 } }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={normalizeCastCode(pinInput).length !== 20}
                onClick={() => {
                  yomiriP2P.connectToHost(pinInput);
                  setShowPinBox(false);
                }}
                sx={{ height: 36, textTransform: 'none', fontSize: 12, bgcolor: 'var(--y-primary, #ff375f)', color: '#ffffff', fontWeight: 750 }}
              >
                Vincular
              </Button>
            </Box>
          )}
        </Box>

        {/* QR Scanner Modal for Mobile Camera */}
        <YomiriQrScannerModal
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onScan={(code) => {
            setPinInput(code);
            yomiriP2P.connectToHost(code);
          }}
        />

        {/* Quick Search for TV */}
        {p2pState==='connected' && <Button size="small" onClick={()=>yomiriP2P.disconnect()} sx={{mb:1}}>Desvincular TV</Button>}
        {p2pState==='connected' && <Typography sx={{fontSize:12,mb:2}}>Mantené Yomiri abierto en el móvil mientras usás la TV.</Typography>}
        {linkError && <Typography role="alert" sx={{fontSize:12,color:'var(--y-error)',mb:2}}>{linkError}</Typography>}
        <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar manga o anime en la TV…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'var(--y-onSurfaceVariant)', fontSize: 19 }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  fontSize: 13,
                  color: '#ffffff',
                },
              },
            }}
          />
        </Box>

        {/* Media Controls (Next/Prev Page & Play/Pause) */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--y-onSurfaceVariant)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Reproducción y Lectura
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
          <ButtonBase
            onClick={() => dispatchCast({ type: 'READER_CONTROL', action: 'prev' })}
            sx={{ flex: 1, py: 1.2, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}
          >
            <SkipPreviousIcon sx={{ fontSize: 20, mr: 0.5 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Anterior</Typography>
          </ButtonBase>
          <ButtonBase
            onClick={() => {
              const next = !playing;
              setPlaying(next);
              dispatchCast({ type: 'PLAYER_CONTROL', action: next ? 'play' : 'pause' });
            }}
            sx={{ flex: 1, py: 1.2, borderRadius: '8px', bgcolor: 'rgba(255, 55, 95, 0.15)', border: '1px solid rgba(255, 55, 95, 0.3)', color: '#ff5470' }}
          >
            {playing ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}
          </ButtonBase>
          <ButtonBase
            onClick={() => dispatchCast({ type: 'READER_CONTROL', action: 'next' })}
            sx={{ flex: 1, py: 1.2, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Siguiente</Typography>
            <SkipNextIcon sx={{ fontSize: 20, ml: 0.5 }} />
          </ButtonBase>
        </Box>

        {/* TV Quick Controls: Volume & Fullscreen */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
          <ButtonBase
            onClick={() => dispatchCast({ type: 'PLAYER_CONTROL', action: 'volume_down' })}
            sx={{ flex: 1, py: 1, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}
          >
            <VolumeDownIcon sx={{ fontSize: 18 }} />
          </ButtonBase>
          <ButtonBase
            onClick={() => dispatchCast({ type: 'PLAYER_CONTROL', action: 'mute' })}
            sx={{ flex: 1, py: 1, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}
          >
            <VolumeOffIcon sx={{ fontSize: 18 }} />
          </ButtonBase>
          <ButtonBase
            onClick={() => dispatchCast({ type: 'PLAYER_CONTROL', action: 'volume_up' })}
            sx={{ flex: 1, py: 1, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}
          >
            <VolumeUpIcon sx={{ fontSize: 18 }} />
          </ButtonBase>
          <ButtonBase
            onClick={() => dispatchCast({ type: 'PLAYER_CONTROL', action: 'toggle_fullscreen' })}
            sx={{ flex: 1, py: 1, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}
          >
            <FullscreenIcon sx={{ fontSize: 18 }} />
          </ButtonBase>
        </Box>

        {/* Virtual D-Pad for TV Navigation */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--y-onSurfaceVariant)', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
          Cruceta D-Pad
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
          <ButtonBase onClick={() => sendKey('ArrowUp')} sx={{ width: 56, height: 42, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <KeyboardArrowUp />
          </ButtonBase>
          <Box sx={{ display: 'flex', gap: 0.8 }}>
            <ButtonBase onClick={() => sendKey('ArrowLeft')} sx={{ width: 56, height: 42, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <KeyboardArrowLeft />
            </ButtonBase>
            <ButtonBase onClick={() => sendKey('Enter')} sx={{ width: 56, height: 42, borderRadius: '8px', bgcolor: 'var(--y-primary, #ff375f)', color: '#ffffff', fontWeight: 800, fontSize: 12 }}>
              OK
            </ButtonBase>
            <ButtonBase onClick={() => sendKey('ArrowRight')} sx={{ width: 56, height: 42, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <KeyboardArrowRight />
            </ButtonBase>
          </Box>
          <ButtonBase onClick={() => sendKey('ArrowDown')} sx={{ width: 56, height: 42, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <KeyboardArrowDown />
          </ButtonBase>
        </Box>
      </Drawer>
    </>
  );
}
